import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import {
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";

import {
  formatVolume,
  formatWeight,
} from "../../utils/number-format.js";

import {
  MapLayerControl,
  defaultLayerVisibility,
} from "./MapLayerControl.jsx";
import { MapStatusOverlay } from "./MapStatusOverlay.jsx";
import {
  RoutePointList,
  buildRoutePoints,
} from "./RoutePointList.jsx";

const DEFAULT_TILE_URL = "/api/map/tile/{z}/{x}/{y}.png";
const DEFAULT_ATTRIBUTION = "© OpenStreetMap contributors";
const DEFAULT_CENTER = [-6.966, 110.416];

// FLEET_MAP_TILE_RELIABILITY_V1
const STANDARD_OSM_TILE_URL_PATTERN =
  /^https?:\/\/(?:\{s\}\.)?tile\.openstreetmap\.org\//i;

function resolveFleetMapTileUrl(value) {
  const requested = String(value || "").trim();

  if (!requested || STANDARD_OSM_TILE_URL_PATTERN.test(requested)) {
    return DEFAULT_TILE_URL;
  }

  return requested;
}

function handleFleetMapTileError(event) {
  const tile = event?.tile;

  if (!tile || tile.dataset.routePlanningTileRetry === "1") {
    return;
  }

  const source = tile.currentSrc || tile.src;

  if (!source) return;

  tile.dataset.routePlanningTileRetry = "1";

  try {
    const retryUrl = new URL(source, window.location.href);

    retryUrl.searchParams.set("_rp_retry", "1");
    retryUrl.searchParams.set("_rp_ts", String(Date.now()));

    tile.src = retryUrl.toString();
  } catch {
    // Keep Leaflet's normal error handling when the retry URL cannot be built.
  }
}
const LAYER_STORAGE_KEY = "route-planning-map-layers-v1";
const LAYER_PANEL_STORAGE_KEY =
  "route-planning-map-layer-panel-collapsed-v1";

const numberFormatter = new Intl.NumberFormat("id-ID", {
  maximumFractionDigits: 1,
});

const POI_SYMBOLS = {
  HOTEL: "H",
  FUEL_STATION: "S",
  WORKSHOP: "B",
  REST_AREA: "R",
  OFFICE: "K",
  TRANSIT: "T",
  PARTNER: "P",
  OTHER: "L",
};

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function isValidCoordinate(point) {
  const latitude = Number(point?.latitude);
  const longitude = Number(point?.longitude);

  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
}

function toLatLng(point) {
  return [Number(point.latitude), Number(point.longitude)];
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function safeColor(value, fallback = "#475569") {
  const color = String(value || "").trim();
  return /^#[0-9a-f]{6}$/i.test(color) ? color : fallback;
}

function formatDistance(meters) {
  if (meters === null || meters === undefined || !Number.isFinite(Number(meters))) {
    return "—";
  }

  if (Number(meters) < 1000) return `${Math.round(Number(meters))} m`;
  return `${numberFormatter.format(Number(meters) / 1000)} km`;
}

function formatDuration(seconds) {
  if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds))) {
    return "—";
  }

  const totalMinutes = Math.max(1, Math.round(Number(seconds) / 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (!hours) return `${minutes} menit`;
  if (!minutes) return `${hours} jam`;
  return `${hours} jam ${minutes} menit`;
}

function formatTimestamp(value) {
  if (!value) return "Belum dihitung";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) return "Belum dihitung";

  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function coordinateSourceLabel(value) {
  const normalized = String(value || "").toUpperCase();

  if (normalized === "ACCURATE") return "Accurate";
  if (normalized === "MANUAL") return "Manual";
  if (normalized.includes("SHIPPING")) return "Alamat Kirim";
  if (normalized.includes("CUSTOMER")) return "Pelanggan";
  return value || "—";
}

function coordinateStatusLabel(value) {
  const normalized = String(value || "MISSING").toUpperCase();

  if (normalized === "VALID") return "Valid";
  if (normalized === "NEEDS_REVIEW") return "Perlu diperiksa";
  if (normalized === "SUSPECTED_SWAPPED") return "Diduga tertukar";
  if (normalized === "OUT_OF_RANGE") return "Di luar rentang";
  if (normalized === "INCOMPLETE") return "Belum lengkap";
  return "Belum tersedia";
}

function hasPersistedStopSequence(payload, stops) {
  const summaryReady = payload?.fleetOrder?.operationalSummary?.stopSequenceReady;

  if (typeof summaryReady === "boolean") return summaryReady;
  if (stops.length <= 1) return true;

  const explicitFlags = stops
    .map((stop) => stop?.sequenceReady)
    .filter((value) => typeof value === "boolean");

  if (explicitFlags.length === stops.length) {
    return explicitFlags.every(Boolean);
  }

  return stops.every(
    (stop) =>
      stop?.persistedSequence !== null &&
      stop?.persistedSequence !== undefined,
  );
}

function routeStatusLabel(value) {
  const normalized = String(value || "NOT_CALCULATED").toUpperCase();

  if (normalized === "READY") return "Siap";
  if (normalized === "STALE") return "Perlu diperbarui";
  if (normalized === "CALCULATING") return "Menghitung";
  if (normalized === "BLOCKED") return "Terblokir";
  if (normalized === "ERROR") return "Gagal";
  if (normalized === "PREVIEW" || normalized === "OPTIMIZED_PREVIEW") return "Preview";
  return "Belum dihitung";
}

function renumberStops(stops) {
  return safeArray(stops).map((stop, index) => ({
    ...stop,
    displaySequence: index + 1,
  }));
}

function stopSignature(stops) {
  return safeArray(stops).map((stop) => stop.key).join("||");
}

function orderStopsByKeys(stops, keys) {
  const stopByKey = new Map(safeArray(stops).map((stop) => [stop.key, stop]));

  return renumberStops(
    safeArray(keys)
      .map((key) => stopByKey.get(key))
      .filter(Boolean),
  );
}

function moveStop(stops, index, direction) {
  const targetIndex = index + direction;

  if (targetIndex < 0 || targetIndex >= stops.length) return stops;

  const next = [...stops];
  const [selected] = next.splice(index, 1);
  next.splice(targetIndex, 0, selected);

  return renumberStops(next);
}

function createStopIcon(stop, selected) {
  const status = String(stop.coordinateStatus || "MISSING")
    .toLowerCase()
    .replaceAll("_", "-");

  return L.divIcon({
    className: `route-marker route-marker--stop route-marker--${status} ${
      selected ? "is-selected" : ""
    }`.trim(),
    html: `<span>${escapeHtml(stop.displaySequence || "")}</span>`,
    iconAnchor: [17, 17],
    iconSize: [34, 34],
    popupAnchor: [0, -18],
  });
}

function createDepotIcon(selected) {
  return L.divIcon({
    className: `route-marker route-marker--depot ${selected ? "is-selected" : ""}`.trim(),
    html: "<span>D</span>",
    iconAnchor: [18, 18],
    iconSize: [36, 36],
    popupAnchor: [0, -18],
  });
}

function createPoiIcon(poi, selected) {
  const color = safeColor(poi.markerColor);
  const symbol = POI_SYMBOLS[poi.poiCategory] || "•";

  return L.divIcon({
    className: `route-marker route-marker--poi ${selected ? "is-selected" : ""}`.trim(),
    html: `<span style="--poi-color:${color}">${escapeHtml(symbol)}</span>`,
    iconAnchor: [14, 14],
    iconSize: [28, 28],
    popupAnchor: [0, -16],
  });
}

function geometryToPositions(route) {
  const coordinates = route?.geometry?.coordinates;

  if (!Array.isArray(coordinates)) return [];

  return coordinates
    .map((coordinate) => {
      const [longitude, latitude] = coordinate || [];
      return [Number(latitude), Number(longitude)];
    })
    .filter(
      ([latitude, longitude]) =>
        Number.isFinite(latitude) && Number.isFinite(longitude),
    );
}

function getInitialCenter({ depot, stops, mapConfig }) {
  const firstValid = [depot, ...safeArray(stops)].find(isValidCoordinate);

  if (firstValid) return toLatLng(firstValid);

  const defaultCenter = mapConfig?.defaultCenter;
  if (defaultCenter && isValidCoordinate(defaultCenter)) {
    return toLatLng(defaultCenter);
  }

  return DEFAULT_CENTER;
}

function redrawFleetMapTiles(map) {
  map.eachLayer((layer) => {
    if (
      layer instanceof L.TileLayer &&
      typeof layer.redraw === "function"
    ) {
      layer.redraw();
    }
  });
}

function MapViewportRecovery({ trigger }) {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();

    if (!container) return undefined;

    let recoveryTimer = 0;
    let initialRedrawTimer = 0;
    let isVisible = true;
    let lastRedrawAt = 0;

    function recover({ redrawTiles = false } = {}) {
      map.invalidateSize({
        animate: false,
        pan: false,
      });

      const now = Date.now();

      if (
        redrawTiles &&
        now - lastRedrawAt >= 600
      ) {
        redrawFleetMapTiles(map);
        lastRedrawAt = now;
      }
    }

    function scheduleRecovery(
      redrawTiles = false,
      delay = 160,
    ) {
      if (recoveryTimer) {
        window.clearTimeout(recoveryTimer);
      }

      recoveryTimer = window.setTimeout(() => {
        recoveryTimer = 0;

        if (!isVisible) return;

        recover({ redrawTiles });
      }, delay);
    }

    function onScroll() {
      if (!isVisible) return;

      scheduleRecovery(true, 160);
    }

    function onResize() {
      scheduleRecovery(true, 80);
    }

    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        scheduleRecovery(true, 60);
      }
    }

    const resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(() => {
            if (isVisible) {
              scheduleRecovery(false, 70);
            }
          })
        : null;

    resizeObserver?.observe(container);

    const intersectionObserver =
      typeof IntersectionObserver === "function"
        ? new IntersectionObserver(
            ([entry]) => {
              isVisible =
                entry?.isIntersecting !== false;

              if (isVisible) {
                scheduleRecovery(true, 60);
              }
            },
            {
              threshold: 0.01,
            },
          )
        : null;

    intersectionObserver?.observe(container);

    document.addEventListener(
      "scroll",
      onScroll,
      true,
    );

    window.addEventListener(
      "resize",
      onResize,
    );

    document.addEventListener(
      "visibilitychange",
      onVisibilityChange,
    );

    scheduleRecovery(false, 50);

    initialRedrawTimer = window.setTimeout(() => {
      if (isVisible) {
        recover({
          redrawTiles: true,
        });
      }
    }, 260);

    return () => {
      if (recoveryTimer) {
        window.clearTimeout(recoveryTimer);
      }

      if (initialRedrawTimer) {
        window.clearTimeout(initialRedrawTimer);
      }

      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();

      document.removeEventListener(
        "scroll",
        onScroll,
        true,
      );

      window.removeEventListener(
        "resize",
        onResize,
      );

      document.removeEventListener(
        "visibilitychange",
        onVisibilityChange,
      );
    };
  }, [map, trigger]);

  return null;
}

function FitMapBounds({ points, request }) {
  const map = useMap();

  useEffect(() => {
    const validPoints = safeArray(points).filter(isValidCoordinate);

    if (!validPoints.length) return;

    const bounds = L.latLngBounds(validPoints.map(toLatLng));
    map.fitBounds(bounds, {
      animate: true,
      maxZoom: 15,
      padding: [36, 36],
    });
  }, [map, points, request]);

  return null;
}

function FocusSelectedPoint({ point }) {
  const map = useMap();

  useEffect(() => {
    if (!isValidCoordinate(point)) return;

    map.flyTo(toLatLng(point), Math.max(map.getZoom(), 14), {
      duration: 0.45,
    });
  }, [map, point]);

  return null;
}

function RouteSummary({ payload, route }) {
  const fleetOrder = payload?.fleetOrder || {};
  const routeStatus = String(route?.status || "NOT_CALCULATED").toUpperCase();
  const routeStatusTone = ["STALE", "FAILED", "NOT_CALCULATED"].includes(routeStatus)
    ? "attention"
    : routeStatus === "CALCULATING"
      ? "pending"
      : "current";

  return (
    <section className="route-map-summary">
      <article>
        <span>Total Stop</span>
        <strong>{safeArray(payload?.stops).length}</strong>
      </article>
      <article>
        <span>Jarak</span>
        <strong>{formatDistance(route?.distanceM)}</strong>
      </article>
      <article>
        <span>Estimasi</span>
        <strong>{formatDuration(route?.durationS)}</strong>
      </article>
      <article>
        <span>Berat</span>
        <strong>
          {formatWeight(fleetOrder.totalWeightKg ?? 0)}
        </strong>
      </article>

      <article>
        <span>Volume</span>
        <strong>
          {formatVolume(fleetOrder.totalVolumeM3 ?? 0)}
        </strong>
      </article>
      <article className={`route-map-summary__status route-map-summary__status--${routeStatusTone}`}>
        <span>Status Jalur</span>
        <strong>{routeStatusLabel(route?.status)}</strong>
      </article>
      <article>
        <span>Terakhir dihitung</span>
        <strong>{formatTimestamp(route?.calculatedAt)}</strong>
      </article>
    </section>
  );
}

function DepotPopup({ depot, title = "Depot" }) {
  return (
    <div className="route-map-popup">
      <strong>{title}</strong>
      <h4>{depot.depotName || "Depot"}</h4>
      {depot.addressText && <p>{depot.addressText}</p>}
      <dl>
        <div>
          <dt>Kode</dt>
          <dd>{depot.depotCode || "—"}</dd>
        </div>
        <div>
          <dt>Status koordinat</dt>
          <dd>{coordinateStatusLabel(depot.coordinateStatus)}</dd>
        </div>
      </dl>
    </div>
  );
}

function StopPopup({ stop }) {
  return (
    <div className="route-map-popup">
      <strong>Stop {stop.displaySequence}</strong>
      <h4>{stop.customerName}</h4>
      <p>{stop.addressAlias || "Alamat default"}</p>
      <dl>
        <div>
          <dt>Area</dt>
          <dd>{stop.resolvedArea || "—"}</dd>
        </div>
        <div>
          <dt>Rute</dt>
          <dd>{stop.resolvedRouteName || "—"}</dd>
        </div>
        <div>
          <dt>SO</dt>
          <dd>{safeArray(stop.salesOrderNumbers).join(", ") || "—"}</dd>
        </div>
        <div>
          <dt>Item</dt>
          <dd>{stop.itemCount || 0}</dd>
        </div>
        <div>
          <dt>Berat</dt>
          <dd>{formatWeight(stop.totalWeightKg ?? 0)}</dd>
        </div>
        <div>
          <dt>Volume</dt>
          <dd>{formatVolume(stop.totalVolumeM3 ?? 0)}</dd>
        </div>
        <div>
          <dt>Sumber koordinat</dt>
          <dd>{coordinateSourceLabel(stop.coordinateResolutionSource || stop.coordinateSource)}</dd>
        </div>
        <div>
          <dt>Status koordinat</dt>
          <dd>{coordinateStatusLabel(stop.coordinateStatus)}</dd>
        </div>
      </dl>
    </div>
  );
}

function PoiPopup({ poi }) {
  const fields = [
    ["Alamat", poi.addressText],
    ["Kontak", poi.contactName],
    ["Telepon", poi.contactPhone],
    ["Jam buka", poi.openingHours],
    ["Catatan", poi.notes],
  ].filter(([, value]) => value);

  return (
    <div className="route-map-popup">
      <strong>{poi.poiCategoryLabel || "POI"}</strong>
      <h4>{poi.poiName}</h4>
      {poi.canBeWaypoint && (
        <span className="route-map-popup__badge">Siap jadi waypoint nanti</span>
      )}
      {fields.length > 0 && (
        <dl>
          {fields.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function RouteWorkflowToolbar({
  busy,
  dirty,
  mode,
  onApply,
  onCancel,
  onManual,
  onOptimize,
  onRecalculate,
  onReset,
  readOnly,
  sequenceLocked,
  sequenceLockReason,
  showRecalculate = true,
}) {
  if (readOnly) {
    return (
      <div className="route-workflow-toolbar route-workflow-toolbar--readonly">
        <span>Mode baca saja</span>
      </div>
    );
  }

  if (mode === "optimization") {
    return (
      <div className="route-workflow-toolbar">
        <button
          className="primary-button"
          disabled={busy || sequenceLocked}
          onClick={onApply}
          title={
            sequenceLocked
              ? sequenceLockReason
              : busy
                ? "Rekomendasi sedang disimpan."
                : "Terapkan urutan hasil optimasi."
          }
          type="button"
        >
          {busy ? "Menyimpan…" : "Terapkan Rekomendasi"}
        </button>
        <button
          className="secondary-button"
          disabled={busy || sequenceLocked}
          onClick={onManual}
          title={sequenceLocked ? sequenceLockReason : "Sesuaikan rekomendasi secara manual."}
          type="button"
        >
          Sesuaikan Manual
        </button>
        <button className="secondary-button" disabled={busy} onClick={onCancel} type="button">
          Batalkan Preview
        </button>
      </div>
    );
  }

  if (mode === "manual") {
    return (
      <div className="route-workflow-toolbar">
        <button
          className="primary-button"
          disabled={busy || !dirty || sequenceLocked}
          onClick={onApply}
          title={
            sequenceLocked
              ? sequenceLockReason
              : busy
                ? "Urutan sedang disimpan."
                : dirty
                  ? "Simpan urutan stop yang baru."
                  : "Belum ada perubahan urutan yang perlu disimpan."
          }
          type="button"
        >
          {busy ? "Menyimpan…" : "Simpan Urutan"}
        </button>
        <button className="secondary-button" disabled={busy} onClick={onCancel} type="button">
          Batalkan Perubahan
        </button>
        <button
          className="secondary-button"
          disabled={busy || !dirty || sequenceLocked}
          onClick={onReset}
          title={
            sequenceLocked
              ? sequenceLockReason
              : dirty
                ? "Batalkan susunan saat ini dan kembali ke urutan tersimpan."
                : "Urutan saat ini sudah sama dengan urutan tersimpan."
          }
          type="button"
        >
          Reset ke Urutan Tersimpan
        </button>
      </div>
    );
  }

  return (
    <div className="route-workflow-toolbar">
      <button
        className="primary-button"
        disabled={busy || sequenceLocked}
        onClick={onOptimize}
        title={
          sequenceLocked
            ? sequenceLockReason
            : busy
              ? "Optimasi sedang dihitung."
              : "Hitung rekomendasi urutan stop."
        }
        type="button"
      >
        {busy ? "Menghitung…" : "Optimalkan Urutan"}
      </button>
      <button
        className="secondary-button"
        disabled={busy || sequenceLocked}
        onClick={onManual}
        title={sequenceLocked ? sequenceLockReason : "Atur urutan stop secara manual."}
        type="button"
      >
        Atur Manual
      </button>
      {showRecalculate && (
        <button
          className="secondary-button"
          disabled={busy}
          onClick={onRecalculate}
          title={busy ? "Perhitungan jalur sedang berjalan." : "Hitung jalur dari urutan stop tersimpan."}
          type="button"
        >
          Hitung Ulang Jalur
        </button>
      )}
    </div>
  );
}

function RouteComparisonPanel({ comparison, current, optimized }) {
  if (!comparison || !current || !optimized) return null;

  return (
    <section className="route-comparison-panel">
      <header>
        <h4>Perbandingan Rekomendasi</h4>
        <span>Preview saja, belum tersimpan</span>
      </header>
      <div className="route-comparison-grid">
        <article>
          <span>Urutan Saat Ini</span>
          <strong>{formatDistance(current.distanceM)}</strong>
          <small>{formatDuration(current.durationS)}</small>
        </article>
        <article>
          <span>Rekomendasi</span>
          <strong>{formatDistance(optimized.distanceM)}</strong>
          <small>{formatDuration(optimized.durationS)}</small>
        </article>
        <article>
          <span>Potensi Penghematan</span>
          <strong>{formatDistance(Math.max(0, comparison.distanceSavedM || 0))}</strong>
          <small>
            {formatDuration(Math.max(0, comparison.durationSavedS || 0))} ·{" "}
            {numberFormatter.format(Math.max(0, comparison.distanceSavedPercent || 0))}%
          </small>
        </article>
      </div>
      {comparison.noImprovement && (
        <p>
          Urutan saat ini sudah optimal berdasarkan titik dan profil routing
          yang digunakan.
        </p>
      )}
    </section>
  );
}

function loadStoredLayers(categories) {
  const defaults = defaultLayerVisibility(categories);

  try {
    const stored = JSON.parse(
      window.localStorage.getItem(LAYER_STORAGE_KEY) || "{}",
    );

    return {
      ...defaults,
      ...stored,
    };
  } catch {
    return defaults;
  }
}

function loadStoredLayerPanelCollapsed() {
  try {
    const stored = window.localStorage.getItem(
      LAYER_PANEL_STORAGE_KEY,
    );

    return stored === null ? true : stored === "true";
  } catch {
    return true;
  }
}

export function FleetOrderRouteMap({
  calculating = false,
  error = "",
  mapConfig,
  onOptimize,
  onPreview,
  onRecalculate,
  onRetry,
  onDraftDirtyChange,
  onSaveSequence,
  payload,
  sequenceLocked = false,
  sequenceLockReason = "Urutan tidak dapat diubah karena perjalanan sudah dimulai.",
}) {
  const categories = payload?.poiCategories || [];
  const depot = payload?.depot || null;
  const activeDepots = safeArray(payload?.activeDepots);
  const stops = safeArray(payload?.stops);
  const pois = safeArray(payload?.pois);
  const route = payload?.route || {};
  const readOnly = payload?.mapReadiness?.readOnly === true;
  const sequenceReadOnly = readOnly || sequenceLocked;
  const [mode, setMode] = useState("saved");
  const [draftStops, setDraftStops] = useState([]);
  const [previewRoute, setPreviewRoute] = useState(null);
  const [optimization, setOptimization] = useState(null);
  const [workflowBusy, setWorkflowBusy] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [workflowError, setWorkflowError] = useState("");
  const [workflowMessage, setWorkflowMessage] = useState("");
  const [selectedKey, setSelectedKey] = useState("");
  const [layers, setLayers] = useState(() => loadStoredLayers(categories));
  const [layerPanelCollapsed, setLayerPanelCollapsed] = useState(loadStoredLayerPanelCollapsed);
  const [fitRequest, setFitRequest] = useState({
    mode: "route",
    count: 0,
  });
  const previewRequestId = useRef(0);
  const previewCache = useRef(new Map());

  const savedSignature = useMemo(() => stopSignature(stops), [stops]);
  const draftSignature = useMemo(() => stopSignature(draftStops), [draftStops]);
  const editing = mode === "manual" || mode === "optimization";
  const dirty = editing && draftSignature !== savedSignature;
  const activeStops = editing ? draftStops : stops;
  const activeRoute = previewRoute || route;
  const sequencePersisted = hasPersistedStopSequence(payload, stops);
  const modeLabel =
    mode === "optimization"
      ? "Rekomendasi"
      : mode === "manual"
        ? "Draft manual"
        : sequencePersisted
          ? "Tersimpan"
          : "Belum disimpan";
  const modeDescription =
    mode === "optimization"
      ? "Urutan hasil optimasi"
      : mode === "manual"
        ? "Urutan draft manual"
        : "Urutan tampilan saat ini";

  const routePoints = useMemo(
    () => buildRoutePoints({ depot, stops: activeStops, route: activeRoute }),
    [activeRoute, activeStops, depot],
  );

  const selectedPoint = useMemo(
    () =>
      routePoints.find((point) => point.key === selectedKey) ||
      activeStops.find((stop) => stop.key === selectedKey) ||
      null,
    [activeStops, routePoints, selectedKey],
  );

  const routePositions = useMemo(
    () => geometryToPositions(activeRoute),
    [activeRoute],
  );

  const visiblePoiCategories = useMemo(
    () =>
      new Set(
        categories
          .filter((category) => layers[`poi:${category.value}`])
          .map((category) => category.value),
      ),
    [categories, layers],
  );

  const visiblePois = useMemo(
    () =>
      pois.filter(
        (poi) =>
          isValidCoordinate(poi) &&
          visiblePoiCategories.has(poi.poiCategory),
      ),
    [pois, visiblePoiCategories],
  );

  const otherDepots = useMemo(
    () =>
      activeDepots.filter(
        (item) =>
          isValidCoordinate(item) &&
          item.id !== depot?.id,
      ),
    [activeDepots, depot],
  );

  const routeFitPoints = useMemo(
    () => [depot, ...activeStops].filter(isValidCoordinate),
    [activeStops, depot],
  );

  const allFitPoints = useMemo(
    () => [
      ...routeFitPoints,
      ...otherDepots,
      ...visiblePois,
    ],
    [otherDepots, routeFitPoints, visiblePois],
  );

  const fitPoints =
    fitRequest.mode === "all" ? allFitPoints : routeFitPoints;

  useEffect(() => {
    setLayers((current) => ({
      ...defaultLayerVisibility(categories),
      ...current,
    }));
  }, [categories]);

  useEffect(() => {
    try {
      window.localStorage.setItem(LAYER_STORAGE_KEY, JSON.stringify(layers));
    } catch {
      // Ignore storage restrictions.
    }
  }, [layers]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        LAYER_PANEL_STORAGE_KEY,
        String(layerPanelCollapsed),
      );
    } catch {
      // Ignore storage restrictions.
    }
  }, [layerPanelCollapsed]);

  useEffect(() => {
    setSelectedKey((current) => current || routePoints[0]?.key || "");
  }, [routePoints]);

  useEffect(() => {
    setMode("saved");
    setDraftStops(renumberStops(stops));
    setPreviewRoute(null);
    setOptimization(null);
    setWorkflowError("");
    setWorkflowMessage("");
    previewRequestId.current += 1;
    previewCache.current.clear();
  }, [payload?.fleetOrder?.id, savedSignature]);

  useEffect(() => {
    setFitRequest((current) => ({
      mode: "route",
      count: current.count + 1,
    }));
  }, [payload?.fleetOrder?.id, activeRoute?.calculatedAt, activeStops.length]);

  useEffect(() => {
    onDraftDirtyChange?.(dirty);

    return () => {
      onDraftDirtyChange?.(false);
    };
  }, [dirty, onDraftDirtyChange]);

  const requestPreview = useCallback(
    async (nextStops) => {
      if (!onPreview || !nextStops.length || sequenceReadOnly) return;

      const signature = stopSignature(nextStops);

      if (signature === savedSignature) {
        setPreviewRoute(null);
        return;
      }

      if (previewCache.current.has(signature)) {
        setPreviewRoute(previewCache.current.get(signature));
        return;
      }

      const requestId = previewRequestId.current + 1;
      previewRequestId.current = requestId;
      setPreviewLoading(true);
      setWorkflowError("");

      try {
        const preview = await onPreview(nextStops.map((stop) => stop.key));

        if (previewRequestId.current !== requestId) return;

        const nextRoute = {
          status: "PREVIEW",
          isPreview: true,
          distanceM: preview.distanceM,
          durationS: preview.durationS,
          geometry: preview.geometry,
          legs: preview.legs || [],
          waypoints: preview.waypoints || [],
          calculatedAt: preview.calculatedAt,
          errorMessage: null,
        };

        previewCache.current.set(signature, nextRoute);
        setPreviewRoute(nextRoute);
      } catch (requestError) {
        if (previewRequestId.current !== requestId) return;
        setWorkflowError(requestError.message);
      } finally {
        if (previewRequestId.current === requestId) {
          setPreviewLoading(false);
        }
      }
    },
    [onPreview, savedSignature, sequenceReadOnly],
  );

  useEffect(() => {
    if (mode !== "manual" || !dirty) return undefined;

    const timer = window.setTimeout(() => {
      requestPreview(draftStops);
    }, 420);

    return () => window.clearTimeout(timer);
  }, [dirty, draftStops, mode, requestPreview]);

  function toggleLayer(key) {
    setLayers((current) => ({
      ...current,
      [key]: current[key] === true ? false : true,
    }));
  }

  function selectPoint(point) {
    if (!point?.key) return;
    setSelectedKey(point.key);
  }

  function enterManualMode(sourceStops = activeStops) {
    if (sequenceReadOnly) return;

    setMode("manual");
    setDraftStops(renumberStops(sourceStops));
    setLayerPanelCollapsed(true);
    setWorkflowError("");
    setWorkflowMessage("");
  }

  async function optimizeOrder() {
    if (!onOptimize || sequenceReadOnly) return;

    setWorkflowBusy(true);
    setWorkflowError("");
    setWorkflowMessage("");

    try {
      const result = await onOptimize();
      const optimizedStops = orderStopsByKeys(stops, result.optimized?.orderedStopKeys);
      const nextRoute = {
        status: "OPTIMIZED_PREVIEW",
        isPreview: true,
        distanceM: result.optimized?.distanceM,
        durationS: result.optimized?.durationS,
        geometry: result.optimized?.geometry,
        legs: result.optimized?.legs || [],
        waypoints: result.optimized?.waypoints || [],
        calculatedAt: result.optimized?.calculatedAt || result.calculatedAt,
        errorMessage: null,
      };

      setDraftStops(
        optimizedStops.length
          ? optimizedStops
          : renumberStops(stops),
      );
      setPreviewRoute(nextRoute);
      setOptimization(result);
      setMode("optimization");
      setLayerPanelCollapsed(true);
      setWorkflowMessage(
        result.comparison?.noImprovement
          ? "Urutan saat ini sudah optimal berdasarkan titik dan profil routing yang digunakan."
          : "Rekomendasi urutan siap ditinjau.",
      );
    } catch (requestError) {
      setWorkflowError(requestError.message);
    } finally {
      setWorkflowBusy(false);
    }
  }

  function cancelDraft() {
    previewRequestId.current += 1;
    setMode("saved");
    setDraftStops(renumberStops(stops));
    setPreviewRoute(null);
    setOptimization(null);
    setWorkflowError("");
    setWorkflowMessage("");
  }

  function updateDraftStops(nextStops, { preview = true } = {}) {
    const renumbered = renumberStops(nextStops);
    setDraftStops(renumbered);
    setWorkflowMessage("");

    if (!preview) {
      previewRequestId.current += 1;
    }

    if (stopSignature(renumbered) === savedSignature) {
      setPreviewRoute(null);
    }
  }

  function moveDraftStop(index, direction) {
    updateDraftStops(moveStop(draftStops, index, direction));
  }

  async function applyDraft() {
    if (!onSaveSequence || sequenceReadOnly) return;

    setWorkflowBusy(true);
    setWorkflowError("");
    setWorkflowMessage("");

    try {
      await onSaveSequence(draftStops);
      setMode("saved");
      setOptimization(null);
      setPreviewRoute(null);
      setWorkflowMessage("Urutan rute berhasil disimpan.");
    } catch (requestError) {
      setWorkflowError(requestError.message);
    } finally {
      setWorkflowBusy(false);
    }
  }

  if (!payload) {
    return (
      <div className="fleet-map-loading">
        <span className="loading-spinner" />
        Memuat peta Fleet Order…
      </div>
    );
  }

  const center = getInitialCenter({ depot, stops: activeStops, mapConfig });
  const routeClassName =
    activeRoute.status === "STALE"
      ? "route-polyline-main route-polyline-main--stale"
      : "route-polyline-main";
  const combinedBusy = calculating || workflowBusy || previewLoading;
  const combinedError = workflowError || error;

  return (
    <div className="fleet-route-map-workspace">
      <RouteSummary payload={{ ...payload, stops: activeStops }} route={activeRoute} />

      <div className="fleet-route-map-layout">
        <aside className="fleet-route-map-sidebar">
          <RouteWorkflowToolbar
            busy={combinedBusy}
            dirty={dirty}
            mode={mode}
            onApply={applyDraft}
            onCancel={cancelDraft}
            onManual={() => enterManualMode(activeStops)}
            onOptimize={optimizeOrder}
            onRecalculate={() => onRecalculate?.({ force: true })}
            onReset={() => updateDraftStops(stops, { preview: false })}
            readOnly={readOnly}
            sequenceLocked={sequenceLocked}
            sequenceLockReason={sequenceLockReason}
            showRecalculate={String(activeRoute?.status || "").toUpperCase() !== "STALE"}
          />

          {sequenceLocked && !readOnly && (
            <div className="route-sequence-lock-notice" role="status">
              {sequenceLockReason}
            </div>
          )}

          {dirty && (
            <div className="route-draft-status">
              Perubahan belum disimpan.
            </div>
          )}

          {previewLoading && (
            <div className="route-draft-status route-draft-status--loading">
              Memperbarui preview jalur…
            </div>
          )}

          {workflowMessage && (
            <div className="notice-banner route-workflow-message">
              {workflowMessage}
            </div>
          )}

          {workflowError && (
            <div className="error-banner route-workflow-message">
              {workflowError}
            </div>
          )}

          <RoutePointList
            depot={depot}
            editable={mode === "manual"}
            modeDescription={modeDescription}
            modeLabel={modeLabel}
            onMoveStop={moveDraftStop}
            onReorderStops={updateDraftStops}
            onSelectPoint={selectPoint}
            route={activeRoute}
            selectedKey={selectedKey}
            stops={activeStops}
          />

          <RouteComparisonPanel
            comparison={optimization?.comparison}
            current={optimization?.current}
            optimized={optimization?.optimized}
          />

          <MapLayerControl
            categories={categories}
            collapsed={layerPanelCollapsed}
            layers={layers}
            onToggleCollapsed={() =>
              setLayerPanelCollapsed((current) => !current)
            }
            onToggleLayer={toggleLayer}
            poiSummary={payload.poiSummary}
          />
        </aside>

        <section className="fleet-route-map-panel" aria-label="Peta rute Fleet Order">
          <div className="fleet-route-map-toolbar">
            <button
              className="secondary-button"
              onClick={() =>
                setFitRequest((current) => ({
                  mode: "route",
                  count: current.count + 1,
                }))
              }
              type="button"
            >
              Fokus Jalur
            </button>
            <button
              className="secondary-button"
              onClick={() =>
                setFitRequest((current) => ({
                  mode: "all",
                  count: current.count + 1,
                }))
              }
              type="button"
            >
              Semua Titik
            </button>
          </div>

          <MapContainer
            center={center}
            className="fleet-route-leaflet"
            scrollWheelZoom
            zoom={mapConfig?.defaultZoom || 11}
          >
            <TileLayer
              attribution={mapConfig?.attribution || DEFAULT_ATTRIBUTION}
              eventHandlers={{
                tileerror: handleFleetMapTileError,
              }}
              url={resolveFleetMapTileUrl(mapConfig?.tileUrl)}
            />
            <MapViewportRecovery trigger={`${payload.fleetOrder?.id}-${activeRoute?.status}-${activeRoute?.calculatedAt}`} />
            <FitMapBounds points={fitPoints} request={`${fitRequest.mode}-${fitRequest.count}`} />
            <FocusSelectedPoint point={selectedPoint} />

            {layers.route !== false && routePositions.length > 1 && (
              <>
                <Polyline
                  pathOptions={{
                    className: "route-polyline-casing",
                    color: "#e0edff",
                    opacity: 0.9,
                    weight: 9,
                  }}
                  positions={routePositions}
                />
                <Polyline
                  pathOptions={{
                    className: routeClassName,
                    color: activeRoute.isPreview ? "#db2777" : "#2563eb",
                    opacity: activeRoute.status === "STALE" ? 0.55 : 0.88,
                    weight: 5,
                  }}
                  positions={routePositions}
                />
              </>
            )}

            {layers.depots !== false && depot && isValidCoordinate(depot) && (
              <Marker
                eventHandlers={{ click: () => selectPoint(routePoints[0]) }}
                icon={createDepotIcon(selectedKey?.startsWith(`depot:${depot.id}`))}
                position={toLatLng(depot)}
              >
                <Popup>
                  <DepotPopup depot={depot} title="Depot awal & kembali" />
                </Popup>
              </Marker>
            )}

            {layers.depots !== false &&
              otherDepots.map((item) => (
                <Marker
                  icon={createDepotIcon(false)}
                  key={item.id}
                  position={toLatLng(item)}
                >
                  <Popup>
                    <DepotPopup depot={item} title="Depot referensi" />
                  </Popup>
                </Marker>
              ))}

            {layers.stops !== false &&
              activeStops.filter(isValidCoordinate).map((stop) => (
                <Marker
                  eventHandlers={{ click: () => selectPoint(stop) }}
                  icon={createStopIcon(stop, selectedKey === stop.key)}
                  key={stop.key}
                  position={toLatLng(stop)}
                >
                  <Popup>
                    <StopPopup stop={stop} />
                  </Popup>
                </Marker>
              ))}

            {visiblePois.map((poi) => (
              <Marker
                icon={createPoiIcon(poi, selectedKey === `poi:${poi.id}`)}
                key={poi.id}
                position={toLatLng(poi)}
              >
                <Popup>
                  <PoiPopup poi={poi} />
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          <MapStatusOverlay
            calculating={calculating}
            error={combinedError}
            onRecalculate={onRecalculate}
            onRetry={onRetry}
            readiness={payload.mapReadiness}
            route={activeRoute}
          />
        </section>
      </div>
    </div>
  );
}
